import { useState } from "react";
import { CircleDot } from "lucide-react";
import type { AuthUser } from "@match/shared";
import { api, errorMessage, tokens } from "@/lib/api";
import { Button, ErrorNote, Field, inputClass } from "@/components/ui";
import { PasswordInput } from "@/components/password-input";

/**
 * IL CAMBIO OBBLIGATO, e prende il posto di tutto il resto.
 *
 * Chi arriva qui ha una password **generata da noi e comunicata su una chat**:
 * l'utente di un cliente appena creato, o chiunque dopo un azzeramento. Finché
 * non la cambia, quella password vale — ed è la stessa forma delle credenziali
 * di prova del seed, un accesso con password nota che nessuno è costretto a
 * cambiare.
 *
 * **Non è un banner e non è una voce di menu**: è la schermata che sta al posto
 * del `Layout`, come `Login`. Un banner lo si ignora; una schermata che occupa
 * il posto no — e nessuna pagina del gestionale è nemmeno montata.
 *
 * **Il backend rifiuta comunque** (`lib/must-change-password.ts`), e i due
 * livelli non sono una ridondanza sprecata: qui si evita di mostrare porte
 * chiuse, là si tengono chiuse. Se la barriera stesse solo qui, un `curl` la
 * aggirerebbe.
 */
export function ChangePassword({ user, tenantName, onChanged }: { user: AuthUser; tenantName: string; onChanged: (u: AuthUser) => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [conferma, setConferma] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== conferma) {
      setError("Le due password nuove non coincidono");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await api.changePassword(current, next);
      /*
       * I token NUOVI si salvano subito: `mustChangePassword` viaggia dentro il
       * token, quindi quello di prima direbbe ancora «deve cambiarla» e la
       * guardia del backend terrebbe fuori chi ha appena ubbidito.
       */
      tokens.set(r.accessToken, r.refreshToken);
      onChanged(r.user);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2">
          <span className="flex" aria-hidden>
            <CircleDot className="h-5 w-5 text-prop" strokeWidth={2.5} />
            <CircleDot className="-ml-2 h-5 w-5 text-broker" strokeWidth={2.5} />
          </span>
          <span className="text-sm font-semibold tracking-tight">{tenantName}</span>
        </div>

        <h1 className="text-lg font-semibold tracking-tight">Cambia la password</h1>
        <p className="mt-1 text-sm text-muted">
          La password con cui sei entrato l&apos;abbiamo generata noi e te l&apos;abbiamo comunicata: finché resta
          quella, la conosce anche chi ha letto quel messaggio. Scegline una tua per continuare.
        </p>
        <p className="mt-1 text-xs text-faint">{user.email}</p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <Field label="Password attuale">
            {(id) => (
            <PasswordInput
              id={id}
              autoComplete="current-password"
              className={inputClass}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
            )}
          </Field>
          <Field label="Nuova password" hint="Almeno 12 caratteri">
            {(id) => (
            <PasswordInput
              id={id}
              autoComplete="new-password"
              className={inputClass}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={12}
              required
            />
            )}
          </Field>
          <Field label="Ripeti la nuova password">
            {(id) => (
            <PasswordInput
              id={id}
              autoComplete="new-password"
              className={inputClass}
              value={conferma}
              onChange={(e) => setConferma(e.target.value)}
              minLength={12}
              required
            />
            )}
          </Field>

          <ErrorNote>{error}</ErrorNote>

          <Button type="submit" variant="primary" disabled={busy} className="w-full justify-center">
            {busy ? "Cambio…" : "Cambia password e continua"}
          </Button>
        </form>
      </div>
    </div>
  );
}
